/* Progressive enhancement of Shopify's existing three-level menus. */
(() => {
  if (customElements.get('tmp-mega-menu')) return;
  class TmpMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.surface = this.querySelector('[data-menu-surface]');
      this.shade = this.querySelector('[data-menu-shade]');
      this.card = this.querySelector('[data-menu-card]');
      this.rail = this.querySelector('.tmp-mega__rail');
      this.stage = this.querySelector('.tmp-mega__stage');
      this.rows = [...this.querySelectorAll('[data-menu-row]')];
      this.branches = [...this.querySelectorAll('[data-menu-branch]')];
      this.toggles = [...this.querySelectorAll('[data-menu-toggle]')];
      this.welcome = this.querySelector('[data-menu-welcome]');
      this.header = this.closest('.section-header') || this.closest('.shopify-section');
      this.nav = this.closest('.header__inline-menu');
      this.desktop = matchMedia('(min-width: 990px)');
      this.hover = matchMedia('(hover: hover) and (pointer: fine)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.keyboardMode = false;
      this.setAttribute('data-input', 'mouse');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.setAttribute('aria-controls', this.surface.id);
      this.setAttribute('data-enhanced', '');
      this.select(null);
      this.summary.addEventListener('click', event => {
        clearTimeout(this.openTimer);
        event.preventDefault();
        const fromKeyboard = event.detail === 0;
        if (this.details.open && this.openedByHover) {
          this.openedByHover = false;
        } else if (this.details.open) this.close(false, true);
        else {
          this.open(fromKeyboard);
          this.openedByHover = false;
        }
        // The vendor script also writes ARIA state on Details-* summary clicks.
        queueMicrotask(() => this.summary.setAttribute('aria-expanded', String(this.details.open)));
      }, options);
      this.summary.addEventListener('pointerenter', event => {
        this.cancelClose();
        if (!this.hover.matches || event.pointerType === 'touch') return;
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => this.open(false), 100);
      }, options);
      const usePointer = event => {
        if (event.pointerType === 'touch') return;
        this.keyboardMode = false;
        if (this.getAttribute('data-input') !== 'mouse') this.setAttribute('data-input', 'mouse');
      };
      this.addEventListener('pointerdown', usePointer, options);
      this.addEventListener('pointermove', usePointer, options);
      this.summary.addEventListener('pointerleave', event => {
        clearTimeout(this.openTimer);
        if (event.pointerType !== 'touch' && !this.isSafeTarget(event.relatedTarget)) this.scheduleClose();
      }, options);
      this.addEventListener('pointerleave', event => {
        clearTimeout(this.openTimer);
        if (event.pointerType === 'touch') return;
        if (this.isSafeTarget(event.relatedTarget)) return;
        this.scheduleClose();
      }, options);
      // Keep the current card while crossing other header triggers, including their small gaps.
      this.nav?.addEventListener('pointerover', event => {
        if (this.details.open && this.isSafeTarget(event.target)) this.cancelClose();
      }, options);
      this.nav?.addEventListener('pointerout', event => {
        if (this.details.open && !this.isSafeTarget(event.relatedTarget)) this.scheduleClose();
      }, options);
      this.surface.addEventListener('pointerenter', () => this.cancelClose(), options);
      this.surface.addEventListener('pointerleave', event => {
        if (event.pointerType === 'touch') return;
        if (!this.isSafeTarget(event.relatedTarget)) this.scheduleClose();
      }, options);
      this.rail.addEventListener('pointermove', event => {
        if (!this.hover.matches || event.pointerType === 'touch') return;
        const row = event.target.closest('[data-menu-row]');
        if (!row || !this.rail.contains(row)) return;
        const index = row.dataset.menuRow;
        const towardPanel = this.lastPointerX != null && (this.rtl ? event.clientX < this.lastPointerX - .25 : event.clientX > this.lastPointerX + .25);
        this.lastPointerX = event.clientX;
        if (this.active === index) {
          clearTimeout(this.selectTimer); this.pending = null; return;
        }
        if (this.pending === index && !towardPanel) return;
        clearTimeout(this.selectTimer);
        this.pending = index;
        // Short dwell prevents diagonal travel into the open panel selecting intervening rows.
        this.selectTimer = setTimeout(() => {
          this.pending = null;
          // Never hide a panel that still contains keyboard focus.
          if (!this.keyboardMode || !this.stage.contains(document.activeElement)) this.select(index);
        }, this.active && towardPanel ? 160 : 65);
      }, options);
      this.rail.addEventListener('pointerleave', () => {
        clearTimeout(this.selectTimer);
        this.pending = null;
        this.lastPointerX = null;
      }, options);
      this.toggles.forEach(toggle => toggle.addEventListener('click', () => {
        this.select(toggle.dataset.menuToggle);
        this.branchLink()?.focus();
      }, options));
      this.querySelector('[data-menu-close]').addEventListener('click', () => this.close(true, true), options);
      this.shade.addEventListener('pointerdown', () => this.close(false, true), options);
      this.addEventListener('keydown', event => this.onKey(event), options);
      this.addEventListener('focusout', event => {
        // During a native focus transfer activeElement can briefly be body.
        if (this.contains(event.relatedTarget)) return;
        queueMicrotask(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      }, options);
      document.addEventListener('pointerdown', event => {
        if (this.details.open && !this.contains(event.target)) this.close(false, true);
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.details.open && !event.defaultPrevented) {
          event.preventDefault(); this.close(true);
        }
      }, options);
      this.desktop.addEventListener('change', () => this.close(), options);
      this.motion.addEventListener('change', () => {
        if (!this.motion.matches) return;
        if (this.closing) this.close(this.contains(document.activeElement));
        else this.cancelCardMotion();
      }, options);
      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (!this.details.open) this.finishClose();
      }, options);
    }
    disconnectedCallback() {
      this.close();
      this.controller?.abort();
      this.controller = null;
    }
    open(fromKeyboard) {
      if (!this.desktop.matches) return;
      this.cancelClose();
      if (this.details.open) return;
      const previous = [...document.querySelectorAll('tmp-mega-menu')].filter(menu => menu !== this && menu.details?.open);
      const origin = previous[0]?.summary.getBoundingClientRect();
      this.switchDirection = origin ? Math.sign(this.summary.getBoundingClientRect().left - origin.left) : 0;
      const transferFocus = previous.some(menu => menu.contains(document.activeElement));
      this.keyboardMode = fromKeyboard;
      this.setAttribute('data-input', fromKeyboard ? 'keyboard' : 'mouse');
      this.openedByHover = !fromKeyboard;
      this.select(null);
      // Keep a live replacement before retiring the old card: header/backdrop state never blinks off.
      this.details.open = true;
      previous.forEach(menu => menu.close());
      if (transferFocus) this.summary.focus({ preventScroll: true });
      this.positionCard();
      this.playMotion(true);
      this.summary.setAttribute('aria-expanded', 'true');
      this.header?.classList.add('tmp-menu-open');
      this.header?.querySelector('.header-wrapper')?.classList.add('menu-open');
      const sticky = this.closest('sticky-header');
      if (sticky) sticky.preventHide = true;
      this.openController = new AbortController();
      const options = { signal: this.openController.signal, passive: true };
      const reposition = () => {
        cancelAnimationFrame(this.positionFrame);
        this.positionFrame = requestAnimationFrame(() => this.positionCard());
      };
      window.addEventListener('resize', reposition, options);
      window.addEventListener('scroll', reposition, options);
      window.visualViewport?.addEventListener('resize', reposition, options);
    }
    positionCard() {
      if (!this.isConnected || !this.details.open) return;
      // Read geometry together, then commit positioning variables without interleaved layout reads.
      const trigger = this.summary.getBoundingClientRect();
      const rect = this.header?.getBoundingClientRect() || trigger;
      let bottom = rect.bottom;
      let stripHeight = 0;
      let shippingStrip = null;
      const next = this.header?.nextElementSibling;
      if (next?.matches('.shopify-section')) {
        const strip = next.getBoundingClientRect();
        if (strip.height > 0 && strip.height <= 140 && strip.top <= bottom + 2 && strip.bottom > bottom) {
          bottom = strip.bottom;
          stripHeight = strip.height;
          shippingStrip = next;
        }
      }
      const gap = parseFloat(getComputedStyle(this.surface).getPropertyValue('--tmp-menu-gap')) || 16;
      const top = Math.max(0, bottom);
      const height = window.visualViewport?.height || window.innerHeight;
      const viewport = document.documentElement.clientWidth;
      const pageWidth = this.header?.querySelector('.page-width')?.getBoundingClientRect().width || viewport - 48;
      const container = Math.min(1500, pageWidth, viewport - 48);
      const edge = (viewport - container) / 2;
      const triggers = [...(this.nav?.querySelectorAll('tmp-mega-menu > details > summary, a.header__menu-item') || [])]
        .filter(el => el.getClientRects().length).map(el => el.getBoundingClientRect());
      const bandLeft = Math.max(edge, triggers.length ? Math.min(...triggers.map(r => r.left)) : edge);
      const bandRight = Math.min(viewport - edge, triggers.length ? Math.max(...triggers.map(r => r.right)) : viewport - edge);
      const bandWidth = Math.max(0, bandRight - bandLeft);
      let compactLeft;
      if (this.surface.classList.contains('tmp-mega--compact')) {
        const width = Math.min(760, bandWidth);
        const preferred = this.surface.dataset.menuAlign === 'trigger'
          ? (this.rtl ? trigger.left : trigger.right - width)
          : bandLeft + (bandWidth - width) / 2;
        compactLeft = Math.max(bandLeft, Math.min(preferred, bandRight - width));
      }
      const stripOffset = stripHeight > 0 ? stripHeight + gap : 0;
      this.bindShippingStrip(shippingStrip);
      this.surface.style.setProperty('--tmp-menu-top', `${top}px`);
      this.shade.style.setProperty('--tmp-shade-top', `${top}px`);
      this.surface.style.setProperty('--tmp-menu-band-left', `${bandLeft}px`);
      this.surface.style.setProperty('--tmp-menu-band-width', `${bandWidth}px`);
      this.surface.style.setProperty('--tmp-menu-available', `${Math.max(120, height - top - gap - 48)}px`);
      this.surface.style.setProperty('--tmp-menu-strip-offset', `${stripOffset}px`);
      this.surface.style.setProperty('--tmp-menu-bridge', `${Math.max(stripHeight > 0 ? 0 : gap, top - trigger.bottom + gap - stripOffset)}px`);
      if (compactLeft !== undefined) this.surface.style.setProperty('--tmp-menu-compact-left', `${compactLeft}px`);
    }
    bindShippingStrip(strip) {
      if (this.shippingStrip === strip) return;
      this.stripController?.abort();
      this.stripController = null;
      this.shippingStrip = strip;
      if (!strip) return;
      this.stripController = new AbortController();
      const options = { signal: this.stripController.signal, passive: true };
      strip.addEventListener('pointerenter', () => this.cancelClose(), options);
      strip.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'touch' && !this.isSafeTarget(event.relatedTarget)) this.scheduleClose();
      }, options);
    }
    scheduleClose() {
      clearTimeout(this.closeTimer);
      if (this.keyboardMode && this.contains(document.activeElement)) return;
      this.closeTimer = setTimeout(() => {
        if (this.keyboardMode && this.contains(document.activeElement)) return;
        this.close(this.contains(document.activeElement));
      }, 0);
    }
    isSafeTarget(target) {
      // The backdrop belongs to this element but is outside the card/trigger/gap safe area.
      if (!(target instanceof Element)) return false;
      const headerTarget = this.nav?.contains(target) && !target.closest('[data-menu-surface],[data-menu-shade]');
      return this.summary.contains(target) || this.surface.contains(target) || this.shippingStrip?.contains(target) || headerTarget;
    }
    select(index) {
      clearTimeout(this.selectTimer);
      this.pending = null;
      const focusedBranch = this.branches.find(branch => branch.contains(document.activeElement));
      if (focusedBranch && focusedBranch.dataset.menuBranch !== index) {
        const row = this.rows.find(item => item.dataset.menuRow === index);
        (row?.querySelector('button') || row?.querySelector('a') || this.summary).focus({ preventScroll: true });
      }
      this.active = index;
      const hasBranch = this.branches.some(branch => branch.dataset.menuBranch === index);
      this.branches.forEach(branch => { branch.hidden = branch.dataset.menuBranch !== index; });
      this.toggles.forEach(toggle => toggle.setAttribute('aria-expanded', String(toggle.dataset.menuToggle === index)));
      this.rows.forEach(row => row.toggleAttribute('data-active', row.dataset.menuRow === index));
      this.welcome.hidden = hasBranch;
      this.stage.scrollTop = 0;
    }
    branchLink() {
      return this.branches.find(branch => !branch.hidden)?.querySelector('a');
    }
    onKey(event) {
      this.cancelClose();
      this.keyboardMode = true;
      this.setAttribute('data-input', 'keyboard');
      if (event.key === 'Escape' && this.details.open) {
        event.preventDefault(); event.stopPropagation(); this.close(true);
      } else if (event.target === this.summary && event.key === 'ArrowDown') {
        event.preventDefault(); this.open(true); this.rail.querySelector('a')?.focus();
      } else if (event.key === 'ArrowRight' && event.target.closest('[data-menu-row]')) {
        const toggle = event.target.closest('[data-menu-row]').querySelector('[data-menu-toggle]');
        if (toggle) { event.preventDefault(); this.select(toggle.dataset.menuToggle); this.branchLink()?.focus(); }
      } else if (event.key === 'ArrowLeft' && this.stage.contains(event.target)) {
        event.preventDefault(); this.toggles.find(toggle => toggle.dataset.menuToggle === this.active)?.focus();
      }
    }
    cancelClose() {
      clearTimeout(this.closeTimer);
      if (!this.closing) return;
      this.closing = false;
      this.removeAttribute('data-closing');
      this.playMotion(true, undefined, true);
    }
    canSlide() {
      const mode = this.surface.dataset.menuMotion || 'none';
      if (mode === 'none' || this.motion.matches || !this.card.animate) return false;
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const cores = navigator.hardwareConcurrency;
      const memory = navigator.deviceMemory;
      if (connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return false;
      if ((cores && cores < 4) || (memory && memory < 4) || (connection?.downlink > 0 && connection.downlink < 2)) return false;
      // Missing/coarse hints are not a benchmark: automatic mode takes the safe instant fallback.
      return mode === 'slide' || (mode === 'auto' && cores >= 4 && memory >= 4 && connection?.effectiveType === '4g' && connection.downlink >= 2);
    }
    cancelCardMotion() {
      this.animation?.cancel(); this.animation = null;
      this.extraAnimations?.forEach(animation => animation.cancel()); this.extraAnimations = [];
      this.removeAttribute('data-sliding');
    }
    playMotion(show, complete, resume = false) {
      if (!this.canSlide()) { this.cancelCardMotion(); complete?.(); return; }
      const current = this.animation?.effect?.target === this.card ? getComputedStyle(this.card).transform : null;
      this.cancelCardMotion();
      const horizontal = show && this.switchDirection && !resume;
      const targets = horizontal ? [this.rail, this.stage].filter(el => el.clientHeight > 0) : [this.card];
      const start = horizontal ? `translateX(${this.switchDirection * 24}px)` : current || (show ? 'translateY(calc(-100% - 40px))' : 'translateY(0)');
      const finish = show ? 'translate(0)' : 'translateY(calc(-100% - 40px))';
      this.setAttribute('data-sliding', '');
      const animations = targets.map(target => target.animate([{ transform: start }, { transform: finish }], {
        duration: horizontal ? 140 : (show ? 180 : 160), easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards'
      }));
      const animation = animations[0];
      this.animation = animation;
      this.extraAnimations = animations.slice(1);
      animation.finished.then(() => {
        if (this.animation !== animation) return;
        this.cancelCardMotion();
        complete?.();
      }).catch(() => {});
    }
    close(restoreFocus = false, animate = false) {
      clearTimeout(this.openTimer);
      if (!this.details) return;
      if (animate && this.details.open && this.canSlide()) {
        if (this.closing) return;
        this.closing = true;
        this.setAttribute('data-closing', '');
        this.playMotion(false, () => this.close(restoreFocus));
        return;
      }
      const wasOpen = this.details.open;
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      this.finishClose();
      if (restoreFocus && wasOpen) this.summary.focus({ preventScroll: true });
    }
    finishClose() {
      this.cancelCardMotion();
      this.closing = false; this.removeAttribute('data-closing');
      clearTimeout(this.openTimer); clearTimeout(this.closeTimer); clearTimeout(this.selectTimer);
      cancelAnimationFrame(this.positionFrame);
      this.openController?.abort();
      this.bindShippingStrip(null);
      this.keyboardMode = false; this.openedByHover = false; this.pending = null;
      const anyOpen = !!this.header?.querySelector('tmp-mega-menu > details[open]');
      this.header?.classList.toggle('tmp-menu-open', anyOpen);
      this.header?.querySelector('.header-wrapper')?.classList.toggle('menu-open', anyOpen);
      const sticky = this.closest('sticky-header');
      if (sticky) sticky.preventHide = anyOpen;
    }
  }
  customElements.define('tmp-mega-menu', TmpMegaMenu);
})();
